import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, BookOpen } from "lucide-react";
import { PlanCard, PLAN_FALLBACK } from "@/components/chalann/PlanCard";
import { getPlans } from "@/lib/api";

export default function Pricing() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState(PLAN_FALLBACK);
  useEffect(() => { getPlans().then((r) => setPlans(r.plans)).catch(() => {}); }, []);

  return (
    <section id="precios" className="relative py-32 md:py-48 border-t border-white/5" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="font-mono text-[11px] tracking-[0.4em] text-white/40 uppercase mb-4">/ Precios</p>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
            Dos <span className="text-[#ff5a00]">planes</span>.<br />
            Sin letras chiquitas.
          </h2>
          <p className="mt-6 text-white/60 max-w-2xl text-sm md:text-base">
            Precios en dólares. Paga con tarjeta (Stripe) o por transferencia SPEI en pesos. Sin periodo de prueba, sin comisiones por venta, sin cobro por dispositivo extra.
          </p>
        </motion.div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {plans.map((p, i) => (
            <motion.div key={p.key} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}>
              <PlanCard
                plan={p}
                onSelect={() => navigate("/setup")}
                cta={
                  <span className={`mt-8 inline-flex items-center gap-2 font-display text-xl tracking-tighter px-6 py-3 rounded-2xl ${p.key === "onepay" ? "bg-[#ccff00] text-black" : "bg-[#ff5a00] text-black"}`} data-testid={`pricing-cta-${p.key}`}>
                    Empezar con {p.name} <ArrowRight size={18} />
                  </span>
                }
              />
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="border border-white/10 rounded-2xl p-6" data-testid="pricing-cap-note">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#ff5a00] uppercase mb-2">CHALANITO · IA</p>
            <p className="font-display text-xl tracking-tighter leading-tight">300 comandos de voz al día</p>
            <p className="mt-2 text-white/50 text-sm leading-relaxed">Suficiente para más de 100 tickets diarios. Al llegar al tope seguimos con match local sin IA; nada se detiene.</p>
          </div>
          <div className="border border-white/10 rounded-2xl p-6" data-testid="pricing-onepay-note">
            <p className="font-mono text-[10px] tracking-[0.3em] text-[#ccff00] uppercase mb-2">ONE PAY · Escáner</p>
            <p className="font-display text-xl tracking-tighter leading-tight">Un pago, un año, sin IA</p>
            <p className="mt-2 text-white/50 text-sm leading-relaxed">Opera con pistola Bluetooth, buscador y cámara. Mismo inventario, fiados, mesas, recibos QR y corte.</p>
            <Link to="/guia-pistola" className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-[#ccff00] hover:underline" data-testid="pricing-guia-link"><BookOpen size={12} /> Guía: pistolas recomendadas y cómo conectarlas</Link>
          </div>
          <div className="border border-white/10 rounded-2xl p-6" data-testid="pricing-compare-note">
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 uppercase mb-2">Comparado con</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-white/70"><span>Loyverse Pro</span><span className="font-mono">≈ $30 USD / mes</span></div>
              <div className="flex justify-between text-white/70"><span>Alegra POS</span><span className="font-mono">≈ $22 USD / mes</span></div>
              <div className="pt-2 mt-2 border-t border-white/10 flex justify-between font-display text-base">
                <span>CHALANN</span><span className="text-[#ccff00]">$20 / mes · $25 / año</span>
              </div>
            </div>
          </div>
        </div>
        <p className="mt-6 text-white/40 text-xs font-mono tracking-widest uppercase leading-relaxed">
          * Precios de la competencia son referencia pública. Ninguno incluye POS por voz nativo en español.
        </p>
      </div>
    </section>
  );
}
